class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.abortController = undefined;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form || !this.input) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    // Never block native GET submit for a non-empty query.
    // Only intercept empty queries, or when a highlighted result *link* is chosen.
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    switch (event.code) {
      case "ArrowUp":
        event.preventDefault();
        this.switchOption("up");
        break;
      case "ArrowDown":
        event.preventDefault();
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) {
      this.statusElement.textContent = "";
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    if (!activeElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit || "10";
    const limitScope = this.dataset.limitScope || "each";
    const qValue = filterTypeQuery
      ? `${filterType}:${filterTypeQuery}+* ${searchTerm}`
      : searchTerm;

    // Percent-encode resources[*] keys so Shopify receives a Hash (avoids 422
    // "expected String to be a Hash: resources"). Omit full-search-only params.
    const params = new URLSearchParams();
    params.set("q", qValue);
    params.set("resources[type]", "product,collection,page,article");
    params.set("resources[limit]", String(limit));
    params.set("resources[limit_scope]", String(limitScope));
    params.set(
      "resources[options][fields]",
      "title,product_type,variants.title,variants.sku,vendor",
    );
    params.set("section_id", "predictive-search");

    const predictiveUrl = `${routes.predictive_search_url}?${params.toString()}`;

    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    fetch(predictiveUrl, { signal })
      .then((response) => {
        if (response.ok) {
          return response.text().then((text) => ({ mode: "native", text }));
        }

        // Japanese (and some other) buyer locales return HTTP 417 for /search/suggest.
        // Fall back to the same section rendered through normal search (search.*).
        if (response.status === 417) {
          return this.fetchFallbackMarkup(searchTerm, signal).then((text) => ({
            mode: "fallback",
            text,
          }));
        }

        const error = new Error(String(response.status));
        error.status = response.status;
        throw error;
      })
      .then((payload) => {
        if (!payload || payload.text == null) {
          this.close();
          return;
        }

        const section = new DOMParser()
          .parseFromString(payload.text, "text/html")
          .querySelector("#shopify-section-predictive-search");

        const resultsMarkup = section ? section.innerHTML.trim() : "";
        if (!resultsMarkup) {
          this.close();
          return;
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error && error.name === "AbortError") {
          return;
        }
        this.close();
      });
  }

  fetchFallbackMarkup(searchTerm, signal) {
    const searchUrl = routes.search_url || window.routes?.search_url || "/search";
    const params = new URLSearchParams();
    params.set("q", searchTerm);
    params.set("type", "product");
    params.set("options[prefix]", "last");
    params.set("section_id", "predictive-search");

    return fetch(`${searchUrl}?${params.toString()}`, { signal }).then((response) => {
      if (!response.ok) {
        const error = new Error(String(response.status));
        error.status = response.status;
        throw error;
      }
      return response.text();
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText || "";

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute("aria-hidden", "true");
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const instanceId = this.dataset.instanceId;
    let markup = resultsMarkup;

    // Namespace result IDs when multiple predictive-search instances exist
    // (header + homepage / search page / 404) so aria-owns/controls stay unique.
    if (instanceId) {
      markup = markup.replace(
        /\bid="predictive-search-/g,
        `id="${instanceId}-predictive-search-`,
      );
      markup = markup.replace(
        /\baria-labelledby="predictive-search-/g,
        `aria-labelledby="${instanceId}-predictive-search-`,
      );
    }

    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const liveRegion = this.querySelector("[data-predictive-search-live-region-count-value]");
    this.setLiveRegionText(liveRegion ? liveRegion.textContent : "");
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    const panel = this.querySelector(".predictive-search");
    if (panel) {
      const top = panel.getBoundingClientRect().top;
      this.predictiveSearchResults.style.maxHeight = `calc(100dvh - ${top}px - 30px)`;
    }

    if (!this.isOpen && typeof publish === "function") {
      publish(PUB_SUB_EVENTS.searchResultsOpen);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.removeAttribute("loading");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen && typeof publish === "function") {
      publish(PUB_SUB_EVENTS.searchResultsClose);
    }

    this.isOpen = false;
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
